import { useEffect, useState } from "react";
import { getPermits } from "../../api/permit";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "../../utils/date";
import BoardToolbar from "../../components/common/board/BoardToolbar";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import SearchBar from "../../components/common/board/SearchFilterBar";
import ExcelUpload from "../../components/permit/ExcelUpload";
import PageHeader from "../../components/common/ui/PageHeader";

const STATUS_LABELS = {
  planned: "계획",
  in_progress: "진행중",
  completed: "완료",
  delayed: "지연",
};
const STATUS_STYLES = {
  planned: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  delayed: "bg-red-100 text-red-700",
};
const ALL_STATUS = Object.keys(STATUS_LABELS);

export default function PermitDashboardPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);

  // 기존 상태 필터는 그대로 유지
  const [statuses, setStatuses] = useState(ALL_STATUS);

  // 정렬도 그대로 유지
  const [ordering, setOrdering] = useState("-id");

  // SearchFilterBar 패턴
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("name");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // 검색 필드 옵션
  const SEARCH_FIELDS = [
    { value: "title", label: "제목" },
    { value: "author", label: "작성자" },
    { value: "dns", label: "DNS" },
  ];

  /** 서버 요청 (Daily 패턴 적용) */
  const fetchList = async () => {
    setLoading(true);

    try {
      const params = {
        project: id,
        status: statuses.join(","), // 상태 필터 추가 (중요)
        ordering,
        page,
        page_size: pageSize,
      };

      // SearchBar 조건 반영
      if (q) params[searchField] = q;
      if (dateFilter !== "all") {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      const data = await getPermits(params);

      setRows(data ?? []);
      setTotal(data.total ?? 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchList();
  }, [id, page, pageSize, searchTrigger, statuses, ordering]);

  /** 검색 버튼 */
  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  const toggleStatus = (s) => {
    setStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
    setPage(1);
  };

  /** 상태별 카운트 (서버 데이터 rows 기반) */
  const statusCount = {
    planned: rows.filter((p) => p.status === "planned").length,
    in_progress: rows.filter((p) => p.status === "in_progress").length,
    completed: rows.filter((p) => p.status === "completed").length,
    delayed: rows.filter((p) => p.status === "delayed").length,
  };

  const columns = [
    { key: "name", header: "인허가명" },
    { key: "permit_type", header: "종류" },
    {
      key: "status",
      header: "상태",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-md text-xs font-medium ${STATUS_STYLES[row.status] || "bg-gray-100 text-gray-600"
            }`}
        >
          {STATUS_LABELS[row.status] || row.status}
        </span>
      ),
    },
    {
      key: "planned_date",
      header: "예정 완료일",
      render: (row) =>
        row.planned_date ? formatDate(row.planned_date) : "확정 전",
    },
    {
      key: "actual_date",
      header: "실제 완료일",
      render: (row) =>
        row.actual_date ? formatDate(row.actual_date) : "완료 전",
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <PageHeader>
        인허가 현황
      </PageHeader>
      {/* 기존 Toolbar + 검색필터 병행 */}
      <BoardToolbar
        left={
          <>
            {/* 요약 현황 */}
            <div className="flex flex-wrap gap-3">
              <div className="rounded-md bg-gray-100 px-3 py-1.5 text-sm">
                계획 <span className="ml-1 font-semibold">{statusCount.planned}</span>
              </div>
              <div className="rounded-md bg-blue-100 px-3 py-1.5 text-sm text-blue-700">
                진행중 <span className="ml-1 font-semibold">{statusCount.in_progress}</span>
              </div>
              <div className="rounded-md bg-green-100 px-3 py-1.5 text-sm text-green-700">
                완료 <span className="ml-1 font-semibold">{statusCount.completed}</span>
              </div>
              <div className="rounded-md bg-red-100 px-3 py-1.5 text-sm text-red-700">
                지연 <span className="ml-1 font-semibold">{statusCount.delayed}</span>
              </div>
            </div>
          </>
        }
        right={
          <>

            {/* SearchFilterBar */}
            <SearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              searchFieldOptions={SEARCH_FIELDS}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
            />
          </>
        }
      />

      {/* Excel Upload 그대로 */}
      {/* <ExcelUpload onUploaded={() => fetchList()} /> */}

      {/* Table */}
      <BoardTable
        columns={columns}
        rows={rows}
        loading={loading}
        keyField="id"
        onRowClick={(row) =>
          navigate(`/project/${id}/permit/view/${row.doc_dns}`)
        }
      />

      {/* Pagination */}
      <BoardPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
}
